import axios from 'axios';
import { User, Product, Order, CartItem, DashboardStats, RecurringOrder } from '../types';

const _host = window.location.hostname;
const _isLocalhost = _host === 'localhost' || _host === '127.0.0.1';
const _isSourdoughsy = _host === 'sourdoughsy.com' || _host.endsWith('.sourdoughsy.com');

// On a custom domain we must use an absolute URL back to the Sourdoughsy API
// so the Host header Django receives is sourdoughsy.com, not the custom domain.
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (_isLocalhost ? 'http://127.0.0.1:8001/api' :
   _isSourdoughsy ? '/api' :
   'https://sourdoughsy.com/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 second timeout to prevent indefinite hangs
});

// Public API instance — no auth interceptors, used for public endpoints
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token refresh with queue to prevent concurrent refresh race conditions
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  failedQueue = [];
};

const forceLogout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  window.location.href = '/customer-login';
  // Return a never-resolving promise so components don't render empty state
  return new Promise<never>(() => {});
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') || 
                           originalRequest?.url?.includes('/token/refresh');
    
    if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest._retry) {
      if (isRefreshing) {
        // Another refresh is in progress — wait for it
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api.request(originalRequest));
            },
            reject: () => resolve(forceLogout()),
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/token/refresh/`, {
            refresh: refreshToken,
          });
          const newToken = response.data.access;
          localStorage.setItem('token', newToken);
          if (response.data.refresh) {
            localStorage.setItem('refreshToken', response.data.refresh);
          }
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api.request(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          return forceLogout();
        } finally {
          isRefreshing = false;
        }
      } else {
        isRefreshing = false;
        return forceLogout();
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (username: string, password: string, isAdmin = false) =>
    api.post('/auth/login/', { username, password, is_admin: isAdmin }),
  
  getProfile: () => api.get<User>('/auth/profile/'),
  
  refreshToken: (refresh: string) =>
    api.post('/token/refresh/', { refresh }),

  changePassword: (oldPassword: string, newPassword: string) =>
    api.post('/auth/change-password/', { old_password: oldPassword, new_password: newPassword }),

  getNotificationPreferences: () => api.get('/auth/notification-preferences/'),
  updateNotificationPreferences: (data: Record<string, boolean>) => api.patch('/auth/notification-preferences/', data),
};

export const mfaAPI = {
  getStatus: () => api.get('/auth/mfa/status/'),
  setup: () => api.post('/auth/mfa/setup/'),
  enable: (code: string) => api.post('/auth/mfa/enable/', { code }),
  disable: (code: string) => api.post('/auth/mfa/disable/', { code }),
  verify: (mfaToken: string, code: string) =>
    api.post('/auth/mfa/verify/', { mfa_token: mfaToken, code }),
};

export const productAPI = {
  getProducts: () => api.get<Product[]>('/products/'),
  getProduct: (id: number) => api.get<Product>(`/products/${id}/`),
  getCategories: () => api.get('/categories/'),
};

export const cartAPI = {
  getCartItems: () => api.get<CartItem[]>('/orders/cart/'),
  addToCart: (productId: number, quantity: number) =>
    api.post('/orders/cart/', { product_id: productId, quantity }),
  updateCartItem: (id: number, quantity: number) =>
    api.patch(`/orders/cart/${id}/`, { quantity }),
  removeFromCart: (id: number) => api.delete(`/orders/cart/${id}/`),
  clearCart: () => api.delete('/orders/cart/clear/'),
};

export const orderAPI = {
  getOrders: () => api.get<Order[]>('/orders/'),
  getOrder: (id: number) => api.get<Order>(`/orders/${id}/`),
  createOrder: (cartItems: number[], deliveryDate?: string, notes?: string) =>
    api.post('/orders/', { cart_items: cartItems, delivery_date: deliveryDate || null, notes: notes || '' }),
  duplicateOrder: (id: number, deliveryDate?: string) =>
    api.post(`/orders/${id}/duplicate/`, { delivery_date: deliveryDate || null }),
  editOrder: (id: number, data: { items?: { product_id: number; quantity: number }[]; delivery_date?: string | null; notes?: string }) =>
    api.put(`/orders/${id}/edit/`, data),
  getOrderStats: () => api.get<DashboardStats>('/orders/stats/'),
  getFinancialData: (startDate?: string, endDate?: string) =>
    api.get('/orders/financial-data/', {
      params: { start_date: startDate, end_date: endDate }
    }),
};

export const recurringAPI = {
  getRecurringOrders: () => api.get<RecurringOrder[]>('/orders/recurring/'),
  getRecurringOrder: (id: number) => api.get<RecurringOrder>(`/orders/recurring/${id}/`),
  createRecurringOrder: (data: {
    source_order_id?: number;
    frequency: string;
    next_delivery_date: string;
    end_date?: string | null;
    notes?: string;
    items?: { product_id: number; quantity: number }[];
  }) => api.post<RecurringOrder>('/orders/recurring/', data),
  updateRecurringOrder: (id: number, data: {
    frequency?: string;
    next_delivery_date?: string;
    end_date?: string | null;
    is_active?: boolean;
    notes?: string;
    items?: { product_id: number; quantity: number }[];
  }) => api.put<RecurringOrder>(`/orders/recurring/${id}/`, data),
  deleteRecurringOrder: (id: number) => api.delete(`/orders/recurring/${id}/`),
};

export const companyAPI = {
  getDashboardStats: () => api.get('/company/dashboard/'),
  getTomorrowsBake: () => api.get('/company/tomorrows-bake/'),
  getTodaysDelivery: () => api.get('/company/todays-delivery/'),
  getCustomers: () => api.get('/company/customers/'),
  getCustomersOverview: () => api.get('/company/customers/overview/'),
  getCustomer: (id: number) => api.get(`/company/customers/${id}/`),
  createCustomer: (data: any) => api.post('/company/customers/', data),
  updateCustomer: (id: number, data: any) => api.patch(`/company/customers/${id}/`, data),
  deleteCustomer: (id: number) => api.delete(`/company/customers/${id}/`),
  getAllOrders: (params?: any) => api.get('/company/orders/', { params }),
  getCustomerOrders: (customerId: number) => api.get(`/company/customers/${customerId}/orders/`),
  getCustomerPayments: (customerId: number) => api.get(`/company/customers/${customerId}/payments/`),
  addCustomerPayment: (customerId: number, data: { amount: number; payment_method: string; reference_number?: string; notes?: string }) =>
    api.post(`/company/customers/${customerId}/payments/`, data),
  updateOrderStatus: (orderId: number, status: string, delivery_date?: string | null) => {
    const data: any = { status };
    if (delivery_date !== undefined) data.delivery_date = delivery_date;
    return api.patch(`/company/orders/${orderId}/status/`, data);
  },
  updateOrderDeliveryDate: (orderId: number, delivered_at: string | null) => api.patch(`/company/orders/${orderId}/status/`, { delivered_at }),
  editOrderItems: (orderId: number, items: { id: number; quantity: number }[]) =>
    api.patch(`/company/orders/${orderId}/edit-items/`, { items }),
  getLogo: () => api.get('/company/logo/'),
  uploadLogo: (file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.post('/company/logo/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLogo: () => api.delete('/company/logo/'),
  // Admin (sub-admin) management
  getAdmins: () => api.get('/company/admins/'),
  getStaffList: () => api.get('/company/staff-list/'),
  createAdmin: (data: { username: string; email: string; first_name: string; last_name: string; phone_number?: string; password: string; permissions?: { can_manage_plan: boolean; can_view_financials: boolean; can_manage_admins: boolean } }) =>
    api.post('/company/admins/', data),
  updateAdmin: (id: number, data: any) => api.patch(`/company/admins/${id}/`, data),
  deleteAdmin: (id: number) => api.delete(`/company/admins/${id}/`),
  // Product management
  getProducts: () => api.get<Product[]>('/company/products/'),
  createProduct: (data: any) => api.post('/company/products/', data),
  updateProduct: (id: number, data: any) => api.put(`/company/products/${id}/`, data),
  deleteProduct: (id: number) => api.delete(`/company/products/${id}/`),
  uploadProductImage: (id: number, file: File) => {
    const fd = new FormData(); fd.append('image', file);
    return api.post(`/company/products/${id}/image/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getCategories: () => api.get('/company/categories/'),
  createCategory: (data: { name: string; description?: string }) => api.post('/company/categories/', data),
  deleteCategory: (id: number) => api.delete(`/company/categories/${id}/`),
  getOrderSettings: () => api.get('/company/order-settings/'),
  updateOrderSettings: (data: { min_delivery_lead_days?: number; company_timezone?: string }) => api.patch('/company/order-settings/', data),
  getAddons: () => api.get('/company/addons/'),
  purchaseAddon: (addonId: number) => api.post(`/company/addons/${addonId}/purchase/`),
  // Ingredients (warehouse)
  getIngredients: () => api.get('/ingredients/'),
  createIngredient: (data: any) => api.post('/ingredients/', data),
  updateIngredient: (id: number, data: any) => api.put(`/ingredients/${id}/`, data),
  deleteIngredient: (id: number) => api.delete(`/ingredients/${id}/`),
  // Suppliers
  getSuppliers: () => api.get('/ingredients/suppliers/'),
  createSupplier: (data: any) => api.post('/ingredients/suppliers/', data),
  updateSupplier: (id: number, data: any) => api.put(`/ingredients/suppliers/${id}/`, data),
  deleteSupplier: (id: number) => api.delete(`/ingredients/suppliers/${id}/`),
  // Inventory
  getInventory: () => api.get('/ingredients/inventory/'),
  updateInventoryRecord: (id: number, data: any) => api.patch(`/ingredients/inventory/${id}/`, data),
  // Purchase Orders
  getPurchaseOrders: () => api.get('/ingredients/purchase-orders/'),
  createPurchaseOrder: (data: any) => api.post('/ingredients/purchase-orders/', data),
  updatePurchaseOrder: (id: number, data: any) => api.put(`/ingredients/purchase-orders/${id}/`, data),
  deletePurchaseOrder: (id: number) => api.delete(`/ingredients/purchase-orders/${id}/`),
  receivePurchaseOrder: (id: number, data: any) => api.post(`/ingredients/purchase-orders/${id}/receive/`, data),
  syncInventoryFromReceived: () => api.post('/ingredients/purchase-orders/sync-inventory/'),
  // Kitchen: Starters
  getStarters: () => api.get('/ingredients/starters/'),
  createStarter: (data: any) => api.post('/ingredients/starters/', data),
  updateStarter: (id: number, data: any) => api.put(`/ingredients/starters/${id}/`, data),
  deleteStarter: (id: number) => api.delete(`/ingredients/starters/${id}/`),
  // Kitchen: Recipes
  getRecipes: () => api.get('/ingredients/recipes/'),
  createRecipe: (data: any) => api.post('/ingredients/recipes/', data),
  updateRecipe: (id: number, data: any) => api.put(`/ingredients/recipes/${id}/`, data),
  deleteRecipe: (id: number) => api.delete(`/ingredients/recipes/${id}/`),
  batchOptimize: (data: any) => api.post('/ingredients/batch-optimize/', data),
  recipeCost: (data: any) => api.post('/ingredients/recipe-cost/', data),
  // Production runs
  getProductions: () => api.get('/ingredients/productions/'),
  startProduction: (data: any) => api.post('/ingredients/productions/', data),
  updateProductionStatus: (id: number, data: any) => api.patch(`/ingredients/productions/${id}/`, data),
  // Low-stock alerts
  getLowStockAlerts: () => api.get('/ingredients/low-stock-alerts/'),

  // Webstore
  getStoreSettings: () => api.get('/webstore/settings/'),
  updateStoreSettings: (data: any) => api.patch('/webstore/settings/', data),
  uploadStoreLogo: (file: File) => {
    const fd = new FormData(); fd.append('logo', file);
    return api.post('/webstore/settings/logo/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteStoreLogo: () => api.delete('/webstore/settings/logo/'),
  uploadStoreHero: (file: File) => {
    const fd = new FormData(); fd.append('hero_image', file);
    return api.post('/webstore/settings/hero/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteStoreHero: () => api.delete('/webstore/settings/hero/'),
  getStoreProducts: () => api.get('/webstore/products/'),
  setRetailPublished: (product_ids: number[], retail_published: boolean) =>
    api.patch('/webstore/products/', { product_ids, retail_published }),
  getRetailMembers: () => api.get('/webstore/members/'),
  getRetailMember: (id: number) => api.get(`/webstore/members/${id}/`),
  updateRetailMember: (id: number, data: any) => api.patch(`/webstore/members/${id}/`, data),
  getRetailOrders: () => api.get('/webstore/orders/'),
  getRetailOrder: (id: number) => api.get(`/webstore/orders/${id}/`),
  updateRetailOrder: (id: number, data: any) => api.patch(`/webstore/orders/${id}/`, data),
  // Coupons
  getCoupons: () => api.get('/webstore/coupons/'),
  createCoupon: (data: any) => api.post('/webstore/coupons/', data),
  updateCoupon: (id: number, data: any) => api.patch(`/webstore/coupons/${id}/`, data),
  deleteCoupon: (id: number) => api.delete(`/webstore/coupons/${id}/`),
  // Store Pages
  getStorePages: () => api.get('/webstore/pages/'),
  createStorePage: (data: any) => api.post('/webstore/pages/', data),
  updateStorePage: (id: number, data: any) => api.patch(`/webstore/pages/${id}/`, data),
  deleteStorePage: (id: number) => api.delete(`/webstore/pages/${id}/`),
  // Contact Messages
  getContactMessages: () => api.get('/webstore/contact/'),
  markContactRead: (id: number, is_read: boolean) => api.patch(`/webstore/contact/${id}/`, { is_read }),
  deleteContactMessage: (id: number) => api.delete(`/webstore/contact/${id}/`),
};

// Public storefront API — no auth, used by /store pages
export const storeAPI = (subdomain: string) => ({
  getStore: () => publicApi.get(`/webstore/public/${subdomain}/`),
  register: (data: any) => publicApi.post(`/webstore/public/${subdomain}/register/`, data),
  login: (email: string, password: string) =>
    publicApi.post(`/webstore/public/${subdomain}/login/`, { email, password }),
  getOrders: (token: string) =>
    publicApi.get(`/webstore/public/${subdomain}/orders/`, { headers: { 'X-Retail-Token': token } }),
  placeOrder: (data: any, token?: string) =>
    publicApi.post(`/webstore/public/${subdomain}/orders/`, data,
      token ? { headers: { 'X-Retail-Token': token } } : {}),
  getProfile: (token: string) =>
    publicApi.get(`/webstore/public/${subdomain}/me/`, { headers: { 'X-Retail-Token': token } }),
  updateProfile: (data: any, token: string) =>
    publicApi.patch(`/webstore/public/${subdomain}/me/`, data, { headers: { 'X-Retail-Token': token } }),
  applyCoupon: (code: string, subtotal: number) =>
    publicApi.post(`/webstore/public/${subdomain}/apply-coupon/`, { code, subtotal }),
  getPages: () => publicApi.get(`/webstore/public/${subdomain}/pages/`),
  getPage: (slug: string) => publicApi.get(`/webstore/public/${subdomain}/pages/${slug}/`),
  submitContact: (data: any) => publicApi.post(`/webstore/public/${subdomain}/contact/`, data),
});

// Custom-domain store lookup — no subdomain context needed
export const getStoreByDomain = (domain: string) =>
  publicApi.get(`/webstore/public-by-domain/?domain=${encodeURIComponent(domain)}`);

export const siteConfigAPI = {
  getConfig: () => api.get('/site/config/1/'),
  getPublicConfig: () => publicApi.get('/site/'),
  updateConfig: (data: any) => api.patch('/site/config/1/', data),
  getBanners: () => api.get('/site/banners/'),
  dismissBanner: (bannerId: number) => api.post(`/site/banners/${bannerId}/dismiss/`),
  getPlans: () => publicApi.get('/site/plans/'),
  getPage: (slug: string) => publicApi.get(`/site/pages/${slug}/`),
  getPages: () => publicApi.get('/site/pages/'),
  submitContact: (data: { name: string; email: string; subject: string; message: string }) =>
    publicApi.post('/site/contact/', data),
  // Help Center
  getHelpTopicsPublic: () => publicApi.get('/site/help/'),
  getHelpTopics: () => api.get('/site/help-topics/'),
  getHelpTopic: (id: number) => api.get(`/site/help-topics/${id}/`),
  createHelpTopic: (data: any) => api.post('/site/help-topics/', data),
  updateHelpTopic: (id: number, data: any) => api.patch(`/site/help-topics/${id}/`, data),
  deleteHelpTopic: (id: number) => api.delete(`/site/help-topics/${id}/`),
  // Help FAQs
  getHelpFAQs: () => api.get('/site/help-faqs/'),
  createHelpFAQ: (data: any) => api.post('/site/help-faqs/', data),
  updateHelpFAQ: (id: number, data: any) => api.patch(`/site/help-faqs/${id}/`, data),
  deleteHelpFAQ: (id: number) => api.delete(`/site/help-faqs/${id}/`),
  // User Guide
  getGuideSectionsPublic: () => publicApi.get('/site/guide/'),
  getGuideSections: () => api.get('/site/guide-sections/'),
  getGuideSection: (id: number) => api.get(`/site/guide-sections/${id}/`),
  createGuideSection: (data: any) => api.post('/site/guide-sections/', data),
  updateGuideSection: (id: number, data: any) => api.patch(`/site/guide-sections/${id}/`, data),
  deleteGuideSection: (id: number) => api.delete(`/site/guide-sections/${id}/`),
};

export const stripeAPI = {
  getConfig: () => api.get('/stripe/config/'),
  createCheckoutSession: (planId: number) =>
    api.post('/stripe/create-checkout-session/', { plan_id: planId }),
  getSubscriptionStatus: () => api.get('/stripe/subscription-status/'),
  createCustomerPortal: () => api.post('/stripe/customer-portal/'),
  verifyCheckout: (sessionId: string) =>
    api.post('/stripe/verify-checkout/', { session_id: sessionId }),
};

export const supportAPI = {
  getTickets: () => api.get('/support/tickets/'),
  getTicket: (id: number) => api.get(`/support/tickets/${id}/`),
  createTicket: (data: { subject: string; category: string; priority: string; message: string }) =>
    api.post('/support/tickets/', data),
  reply: (id: number, body: string) =>
    api.post(`/support/tickets/${id}/reply/`, { body }),
  closeTicket: (id: number) =>
    api.post(`/support/tickets/${id}/close/`),
  reopenTicket: (id: number) =>
    api.post(`/support/tickets/${id}/reopen/`),
  getUnreadCount: () => api.get('/support/tickets/unread_count/'),
};

export const customerSupportAPI = {
  getTickets: () => api.get('/support/customer-tickets/'),
  getTicket: (id: number) => api.get(`/support/customer-tickets/${id}/`),
  createTicket: (data: { subject: string; category: string; priority: string; message: string }) =>
    api.post('/support/customer-tickets/', data),
  reply: (id: number, body: string) =>
    api.post(`/support/customer-tickets/${id}/reply/`, { body }),
  closeTicket: (id: number) =>
    api.post(`/support/customer-tickets/${id}/close/`),
  reopenTicket: (id: number) =>
    api.post(`/support/customer-tickets/${id}/reopen/`),
  getUnreadCount: () => api.get('/support/customer-tickets/unread_count/'),
};

export const adminSupportAPI = {
  getTickets: () => api.get('/support/admin-tickets/'),
  getTicket: (id: number) => api.get(`/support/admin-tickets/${id}/`),
  reply: (id: number, body: string) =>
    api.post(`/support/admin-tickets/${id}/reply/`, { body }),
  closeTicket: (id: number) =>
    api.post(`/support/admin-tickets/${id}/close/`),
  reopenTicket: (id: number) =>
    api.post(`/support/admin-tickets/${id}/reopen/`),
  resolveTicket: (id: number) =>
    api.post(`/support/admin-tickets/${id}/resolve/`),
  getUnreadCount: () => api.get('/support/admin-tickets/unread_count/'),
};

export const subdomainAPI = {
  /** Public endpoint — returns company info for the current subdomain */
  getCompanyInfo: () => publicApi.get('/company-info/'),
};

export const emailAPI = {
  getTemplates: () => api.get('/emails/templates/'),
  getTemplate: (id: number) => api.get(`/emails/templates/${id}/`),
  updateTemplate: (id: number, data: any) => api.patch(`/emails/templates/${id}/`, data),
  sendTestEmail: (id: number, toEmail: string) => api.post(`/emails/templates/${id}/test/`, { to_email: toEmail }),
  previewTemplate: (id: number) => api.post(`/emails/templates/${id}/preview/`),
  getLogs: () => api.get('/emails/logs/'),
  getTriggerVariables: () => api.get('/emails/triggers/'),
  requestPasswordReset: (email: string) => publicApi.post('/auth/password-reset/', { email }),
  confirmPasswordReset: (token: string, password: string) => publicApi.post('/auth/password-reset/confirm/', { token, password }),
};

export const adminAPI = {
  getRateLimits: () => api.get('/admin/rate-limits/'),
  updateRateLimits: (data: any) => api.put('/admin/rate-limits/', data),
  getTermsAcceptances: (params?: { page?: number; search?: string }) => api.get('/admin/terms-acceptances/', { params }),
};

export const securityAPI = {
  getEvents: (params?: Record<string, string>) =>
    api.get('/security/events/', { params }),
  getSummary: () => api.get('/security/events/summary/'),
  getChoices: () => api.get('/security/events/choices/'),
  resolveEvent: (id: number) => api.post(`/security/events/${id}/resolve/`),
  unresolveEvent: (id: number) => api.post(`/security/events/${id}/unresolve/`),
  bulkResolve: (ids: number[]) => api.post('/security/events/bulk_resolve/', { ids }),
};

// ── Platform Payments API ─────────────────────────────────────
export const paymentsAPI = {
  // Stripe Connect
  getConnectOnboardingUrl: () => api.get('/payments/connect/onboarding/'),
  getConnectStatus: () => api.get('/payments/connect/status/'),
  disconnectStripe: () => api.post('/payments/connect/disconnect/'),

  // Wholesale payment link (company admin creates, sends to B2B client)
  createWholesaleLink: (orderId: number) =>
    api.post('/payments/wholesale-link/', { order_id: orderId }),

  // Retail: Stripe Hosted Checkout redirect (recommended)
  createRetailCheckout: (subdomain: string, data: any) =>
    publicApi.post(`/payments/retail-checkout/${subdomain}/`, data),
  createRetailCheckoutByDomain: (data: any) =>
    publicApi.post('/payments/retail-checkout/', data),

  // Retail PaymentIntent (for custom in-page Elements)
  createRetailIntent: (subdomain: string, data: any) =>
    publicApi.post(`/payments/retail-intent/${subdomain}/`, data),
  createRetailIntentByDomain: (data: any) =>
    publicApi.post('/payments/retail-intent/', data),
};

export default api;